import React, { useRef } from "react";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";

const libraries = ["places"];

/**
 * Controlled address input backed by Google Places Autocomplete.
 *
 * Once the user picks an address from the dropdown, the input becomes
 * read-only so arbitrary text can never be submitted as an address.
 * A small ✕ button lets the user clear the selection and pick again.
 */
export function AddressAutocompleteInput({
  id,
  value,
  lat,
  lng,
  components,
  onChange,
  placeholder,
  className,
}) {
  const { isLoaded } = useJsApiLoader({
    id: "google-places-autocomplete",
    googleMapsApiKey: process.env.REACT_APP_MAP_KEY,
    libraries,
  });
  const autocompleteRef = useRef(null);

  // True when the current value was geocoded (picked from the dropdown).
  const isGeocoded = lat != null && lng != null;

  function parseAddressComponents(addressComponents = []) {
    const get = (type, useShortName) =>
      addressComponents.find((c) => c.types.includes(type))?.[
        useShortName ? "short_name" : "long_name"
      ] || "";

    const streetNumber = get("street_number");
    const route = get("route");

    return {
      street: [streetNumber, route].filter(Boolean).join(" "),
      city: get("locality") || get("sublocality") || get("postal_town"),
      state: get("administrative_area_level_1", true),
      zipCode: get("postal_code"),
      country: get("country", true),
    };
  }

  // Ensures the country (USA) is always present in both the displayed
  // address and the parsed components — addresses without it don't match
  // correctly when re-geocoded/displayed elsewhere in the app.
  function withCountryFallback(formattedAddress, components) {
    if (components.country || !formattedAddress) {
      return { address: formattedAddress, components };
    }
    return {
      address: `${formattedAddress}, USA`,
      components: { ...components, country: "USA" },
    };
  }

  function handlePlaceChanged() {
    const place = autocompleteRef.current?.getPlace();
    if (!place) return;

    const parsed = parseAddressComponents(place.address_components);
    const { address, components: finalComponents } = withCountryFallback(
      place.formatted_address || place.name || "",
      parsed
    );

    onChange({
      address,
      lat: place.geometry?.location?.lat() ?? null,
      lng: place.geometry?.location?.lng() ?? null,
      components: finalComponents,
    });
  }

  // While the user is still typing (no selection yet), we pass the
  // free-text value upward but keep lat/lng null so the "not geocoded"
  // validation fires on submit.
  function handleTextChange(e) {
    if (isGeocoded) return; // locked after selection
    onChange({
      address: e.target.value,
      lat: null,
      lng: null,
      components: null,
    });
  }

  // Clears the current selection so the user can pick a new address.
  function handleClear() {
    onChange({ address: "", lat: null, lng: null, components: null });
  }

  if (!isLoaded) {
    return (
      <input
        id={id}
        disabled
        placeholder="Loading address search..."
        className={className}
      />
    );
  }

  return (
    <div className="relative">
      <Autocomplete
        onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
        onPlaceChanged={handlePlaceChanged}
        options={{ componentRestrictions: { country: "us" } }}
      >
        <input
          id={id}
          value={value || ""}
          onChange={handleTextChange}
          readOnly={isGeocoded}
          placeholder={placeholder}
          className={className}
          autoComplete="off"
          style={isGeocoded ? { paddingRight: "2.5rem" } : undefined}
        />
      </Autocomplete>
      {isGeocoded && value && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center h-5 w-5 rounded-full bg-gray-300 hover:bg-gray-400 text-gray-600 hover:text-gray-800 transition text-xs leading-none"
          aria-label="Clear address"
        >
          ✕
        </button>
      )}
    </div>
  );
}
